import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth'
import { auth } from 'data/firebase'
import { completeActivation } from 'cloudFunctions'
import { ACTIVATION_EMAIL_STORAGE_KEY } from 'constants/index'
import { Container } from '../../../components/Container'
import { InputText } from '../../../components/Ui-Kits/InputText'
import { Heading, Paragraph } from '../../../components/Ui-Kits/Text'
import Button from '../../../components/Button/Button'
import { Loading, LoadingWrap } from 'components/Shared/Loading'
import { useAppTranslation } from '../../../i18n/hooks'
import { useLocalStorage } from 'hooks/useLocalStorage'
import { useAlert } from 'hooks/useAlert'
import { logError } from 'utils'

const STATE = {
  CHECKING: 'checking',
  ASK_EMAIL: 'ask-email',
  SET_PASSWORD: 'set-password',
  ERROR: 'error',
}

const passwordSchema = t =>
  yup.object({
    password: yup
      .string()
      .required(t('required'))
      .min(6, t('passwordShouldBeMinNumbers', { min: 6 })),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], t('passwordMustBeSame'))
      .required(t('required')),
  })

const emailSchema = t =>
  yup.object({
    email: yup.string().required(t('required')).email(t('shouldBeEmail')),
  })

export const Activate = () => {
  const { t } = useAppTranslation()
  const history = useHistory()
  const alert = useAlert()
  const [state, setState] = useState(STATE.CHECKING)
  const [errorMessage, setErrorMessage] = useState(null)
  const [storedEmail, setStoredEmail] = useLocalStorage(ACTIVATION_EMAIL_STORAGE_KEY, null)

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setErrorMessage(t('activationLinkInvalid'))
      setState(STATE.ERROR)
      return
    }
    if (storedEmail) {
      signIn(storedEmail)
    } else {
      setState(STATE.ASK_EMAIL)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const signIn = async email => {
    setState(STATE.CHECKING)
    try {
      await signInWithEmailLink(auth, email, window.location.href)
      setStoredEmail(email)
      setState(STATE.SET_PASSWORD)
    } catch (error) {
      logError(error, 'signInWithEmailLink')
      setErrorMessage(t('activationLinkInvalid'))
      setState(STATE.ERROR)
    }
  }

  if (state === STATE.CHECKING) {
    return (
      <Layout>
        <LoadingWrap>
          <Loading />
        </LoadingWrap>
      </Layout>
    )
  }

  if (state === STATE.ERROR) {
    return (
      <Layout>
        <Heading color="primary" mt={0}>
          {t('activationFailed')}
        </Heading>
        <Paragraph mb={1.5} mt={0}>
          {errorMessage}
        </Paragraph>
        <Button color="primary" onClick={() => history.replace('/auth/register')}>
          {t('backToSignup')}
        </Button>
      </Layout>
    )
  }

  if (state === STATE.ASK_EMAIL) {
    return <EmailPrompt onSubmit={signIn} />
  }

  return (
    <SetPasswordForm
      onSuccess={() => {
        setStoredEmail(null)
        alert(t('activationCompleted'))
        history.replace('/profile')
      }}
    />
  )
}

const EmailPrompt = ({ onSubmit }) => {
  const { t } = useAppTranslation()
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(emailSchema(t)),
    defaultValues: { email: '' },
  })

  const onFormSubmit = handleSubmit(({ email }) => onSubmit(email))

  return (
    <Layout>
      <Heading color="primary" mt={0}>
        {t('confirmYourEmail')}
      </Heading>
      <Paragraph mb={1.5} mt={0}>
        {t('confirmYourEmailDescription')}
      </Paragraph>
      <form onSubmit={onFormSubmit}>
        <InputText
          {...register('email')}
          placeholder={t('email')}
          label={t('email')}
          noBorder
          error={errors.email?.message}
          autoFocus
        />
        <Button color="primary" type="submit">
          {t('continue')}
        </Button>
      </form>
    </Layout>
  )
}

const SetPasswordForm = ({ onSuccess }) => {
  const { t } = useAppTranslation()
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(passwordSchema(t)),
    defaultValues: { password: '', confirmPassword: '' },
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const onFormSubmit = handleSubmit(async ({ password }) => {
    setLoading(true)
    setError(null)
    try {
      await completeActivation({ password })
      onSuccess()
    } catch (err) {
      logError(err, 'completeActivation')
      setError(t('somethingWentWrong'))
    } finally {
      setLoading(false)
    }
  })

  return (
    <Layout>
      <Heading color="primary" mt={0}>
        {t('setPassword')}
      </Heading>
      <Paragraph mb={1.5} mt={0}>
        {t('setPasswordDescription')}
      </Paragraph>
      <form onSubmit={onFormSubmit}>
        <InputText
          {...register('password')}
          placeholder={t('password')}
          type="password"
          label={t('password')}
          noBorder
          error={errors.password?.message}
        />
        <InputText
          {...register('confirmPassword')}
          placeholder={t('confirmPassword')}
          type="password"
          label={t('confirmPassword')}
          noBorder
          error={errors.confirmPassword?.message}
        />
        {error && (
          <Paragraph color="red" mb={1} mt={0}>
            {error}
          </Paragraph>
        )}
        <Button color="primary" type="submit" disabled={loading}>
          {t('save')}
        </Button>
      </form>
      {loading && (
        <LoadingWrap>
          <Loading />
        </LoadingWrap>
      )}
    </Layout>
  )
}

const Layout = ({ children }) => (
  <StyledContainer transparent>
    <Box>{children}</Box>
  </StyledContainer>
)

EmailPrompt.propTypes = {
  onSubmit: PropTypes.func.isRequired,
}

SetPasswordForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
}

Layout.propTypes = {
  children: PropTypes.node,
}

const Box = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--color-grey);
  padding: 2.5rem;
  position: relative;
`
const StyledContainer = styled(Container)`
  margin-bottom: 5rem;
  max-width: 590px;
`
